import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import ChatPanel, { ChatPanelHandle } from '../components/chat/ChatPanel';
import { useClinicChat } from '../hooks/useClinicChat';
import type { CardData, ChatMessage } from '../utils/chatApi';

type ChatDockContextValue = {
  openChat: () => void;
  closeChat: () => void;
  composeShare: (cardData: CardData) => void;
  startSMSToOwner: (petOwnerId: string, petOwnerName: string, phone: string) => void;
  unreadCount: number;
  isOpen: boolean;
};

const ChatDockContext = createContext<ChatDockContextValue | null>(null);

export const ChatDockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const chatPanelRef = useRef<ChatPanelHandle | null>(null);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  // Only show chat dock where it makes sense (private app surfaces).
  const shouldRenderDock =
    !!user?.practice_id &&
    (location.pathname.startsWith('/dashboard') || location.pathname.includes('/encounter/'));

  useClinicChat({
    enabled: shouldRenderDock,
    isChatOpen,
    onNewMessage: (msg: ChatMessage) => {
      chatPanelRef.current?.addIncomingMessage(msg);
    },
    onMessageEdited: (msg: ChatMessage) => {
      chatPanelRef.current?.updateMessage(msg);
    },
    onMessageUpdated: (msg: ChatMessage) => {
      chatPanelRef.current?.updateMessage(msg);
    },
    onMessageDeleted: (messageId: string) => {
      chatPanelRef.current?.removeMessage(messageId);
    },
  });

  const value = useMemo<ChatDockContextValue>(() => {
    return {
      openChat: () => setIsChatOpen(true),
      closeChat: () => setIsChatOpen(false),
      composeShare: (cardData: CardData) => {
        setIsChatOpen(true);
        chatPanelRef.current?.startDraftWithCard(cardData);
      },
      startSMSToOwner: (petOwnerId: string, petOwnerName: string, phone: string) => {
        setIsChatOpen(true);
        chatPanelRef.current?.startSMSConversation(petOwnerId, petOwnerName, phone);
      },
      unreadCount: chatUnreadCount,
      isOpen: isChatOpen,
    };
  }, [chatUnreadCount, isChatOpen]);

  return (
    <ChatDockContext.Provider value={value}>
      {children}

      {shouldRenderDock && (
        <>
          <ChatPanel
            ref={chatPanelRef}
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
            onUnreadCountChange={setChatUnreadCount}
          />
        </>
      )}
    </ChatDockContext.Provider>
  );
};

export const useChatDock = (): ChatDockContextValue => {
  const ctx = useContext(ChatDockContext);
  if (!ctx) {
    throw new Error('useChatDock must be used within a ChatDockProvider');
  }
  return ctx;
};


